import React from 'react';
import { toast } from 'react-toastify';
import PropertyFormat, { amountFormat } from '../../../../components/commonLayouts/PropertyFormat/PropertyFormat';
import { CalculateFees } from '../../../../components/rfq/common/CalculateFee';
import { CalculateFeesSup } from '../../../../components/rfq/common/CalculateFeeSup';
import { ValidationSchemaForRequestFinancing } from './ValidationSchemaForRequestFinancing';
import {
    ValidationSchemaForRequestFinancingBuyer,
    ValidationSchemaForRequestFinancingSupplier,
} from './ValidationSchemaForRequestFinancingSupplier';

export const getOrgUlid = (ctx) => {
    const platformRole = ctx?.userData?.user_organizations[0]?.user_org_role_maps[0]?.platform_role;
    if (platformRole === 'broker') {
        return ctx?.associatedBuyer;
    }
    if (platformRole === 'buyer') {
        return ctx?.userData?.user_organizations[0]?.organization_ulid;
    }
    return ctx?.userData?.user_organizations[0]?.user_org_role_maps[0]?.organization_ulid;
};

export const getPaymentTermDays = (paymentTerm) => {
    switch (paymentTerm) {
        case 'cod':
            return 0;
        case '7_days':
            return 7;
        case '14_days':
            return 14;
        case '21_days':
            return 21;
        case '28_days':
            return 28;
        case '30_days':
            return 30;
        case '45_days':
            return 45;
        case '60_days':
            return 60;
        default:
            return undefined;
    }
};

export const getFinancingOptionVariable = (platformRole, values) => {
    if (platformRole === 'buyer' || platformRole === 'broker') {
        return values.extended_credit_option;
    }
    return values.early_payment_options;
};

export const getCreditOptFromFinancingOption = (optionVariable) => {
    switch (optionVariable) {
        case 'not_needed':
            return 0;
        case '7_days':
            return 7;
        case '14_days':
            return 14;
        case '21_days':
            return 21;
        case '28_days':
            return 28;
        case '3_days':
            return 0;
        case '15_days':
            return 15;
        case '30_days':
            return 30;
        case '45_days':
            return 45;
        case '60_days':
            return 60;
        case '75_days':
            return 75;
        case '90_days':
            return 90;
        default:
            return 0;
    }
};

const computeSupplierEarlyPaymentInterest = ({
    values,
    organizationDetails,
    platformCharges,
    setBuyerTradeLimit,
}) => {
    const filteredEarlyPaymentFeesArr = organizationDetails?.termSheet?.fee_details?.filter(
        (fee) => fee.fee_type === 'early_payment_fees'
    );
    const matchingFee = (filteredEarlyPaymentFeesArr || []).find(
        (fee) =>
            fee.buyer_organization_ulid ===
            (values?.buyer_supplier_name[0]?.organization_ulid || values?.buyer_supplier_name[0]?.ulid)
    );
    const limitBalance = matchingFee?.trade_support_limit - matchingFee?.trade_support_utilization;
    setBuyerTradeLimit(limitBalance);
    const filteredEarlyPaymentFees = matchingFee ? matchingFee : null;
    let earlyPaymentPercentage = null;
    if (filteredEarlyPaymentFees !== null) {
        if (matchingFee.fee_method === 'sofr_margin') {
            earlyPaymentPercentage = platformCharges?.sofr_rate + matchingFee.fee_value;
        } else if (matchingFee.fee_method === 'percentage') {
            earlyPaymentPercentage = matchingFee.fee_value;
        }
    } else {
        earlyPaymentPercentage = organizationDetails?.termSheet?.early_payment_percent_supplier;
    }

    return earlyPaymentPercentage / 100;
};

export const computeCreditDaysInterest = ({
    creditOpt,
    optionVariable,
    platformRole,
    values,
    organizationDetails,
    platformCharges,
    setBuyerTradeLimit,
}) => {
    if (creditOpt === 0 && optionVariable !== '3_days') {
        const filteredEarlyPaymentFeesArr = organizationDetails?.termSheet?.fee_details?.filter(
            (fee) => fee.fee_type === 'early_payment_fees'
        );
        const matchingFee = filteredEarlyPaymentFeesArr?.find(
            (fee) =>
                fee.buyer_organization_ulid ===
                (values?.buyer_supplier_name[0]?.buyer_organization_ulid || values?.buyer_supplier_name[0]?.ulid)
        );
        const limitBalance = matchingFee?.trade_support_limit - matchingFee?.trade_support_utilization;
        setBuyerTradeLimit(limitBalance);
        return 0;
    }
    if (platformRole === 'buyer' || platformRole === 'broker') {
        let extendedCreditPercentage = null;
        const filteredExtendedPaymentFees = (organizationDetails?.termSheet?.fee_details || []).filter(
            (fee) => fee.fee_type === 'trade_acceleration_charges'
        );
        if (filteredExtendedPaymentFees[0]?.fee_method === 'sofr_margin') {
            extendedCreditPercentage = platformCharges?.sofr_rate + filteredExtendedPaymentFees[0]?.fee_value;
        } else if (filteredExtendedPaymentFees[0]?.fee_method === 'percentage') {
            extendedCreditPercentage = filteredExtendedPaymentFees[0]?.fee_value;
        }
        return extendedCreditPercentage / 100;
    }

    return computeSupplierEarlyPaymentInterest({
        values,
        organizationDetails,
        platformCharges,
        setBuyerTradeLimit,
    });
};

export const accumulateBuyerProductValue = (products, creditDaysInterest, creditOpt) => {
    let totalProductValue = 0;
    products.forEach((product) => {
        const volume = parseFloat(product.volume);
        const price = parseFloat(product.price);
        if (!isNaN(volume) && !isNaN(price)) {
            const priceEarlyPaymentAmount = ((price * creditDaysInterest) / 360) * creditOpt;
            const priceAfterCredit =
                (Math.round((price + parseFloat(((price * creditDaysInterest) / 360) * creditOpt)) * 100) / 100) *
                volume;
            product.priceAfterCrdit = Math.round(priceAfterCredit * 100) / 100;
            product.creditDaysInterest = creditDaysInterest;
            product.priceEarlyPaymentAmount = (Math.round(priceEarlyPaymentAmount * 100) / 100) * volume;
            totalProductValue += priceAfterCredit;
        }
    });
    return totalProductValue;
};

const getSupplierPriceAfterCredit = (price, creditDaysInterest, creditOpt, paymentTermVar, optionVariable) => {
    if (creditOpt === 0 && optionVariable !== '3_days') {
        return price;
    }
    return price - ((price * creditDaysInterest) / 360) * (paymentTermVar - creditOpt);
};

export const accumulateSupplierProductValue = (
    products,
    creditDaysInterest,
    creditOpt,
    paymentTermVar,
    optionVariable
) => {
    let totalProductValue = 0;
    products.forEach((product) => {
        const volume = parseFloat(product.volume);
        const price = parseFloat(product.price);
        if (!isNaN(volume) && !isNaN(price)) {
            const priceEarlyPaymentAmount = ((price * creditDaysInterest) / 360) * (paymentTermVar - creditOpt);
            const priceAfterCredit = getSupplierPriceAfterCredit(
                price,
                creditDaysInterest,
                creditOpt,
                paymentTermVar,
                optionVariable
            );
            product.priceAfterCrdit = (Math.round(priceAfterCredit * 100) / 100) * volume;
            product.creditDaysInterest = creditDaysInterest;
            product.priceEarlyPaymentAmount = (Math.round(priceEarlyPaymentAmount * 100) / 100) * volume;
            totalProductValue += isNaN(product.priceAfterCrdit) ? 0 : Math.round(product.priceAfterCrdit * 100) / 100;
        }
    });
    return totalProductValue;
};

const getBrokerPriceAfterCredit = (commissionType, price, volume, commission, creditDaysInterest, creditOpt) => {
    if (commissionType === 'per_mt') {
        const creditamount = Math.round(((price * creditDaysInterest) / 360) * creditOpt * 100) / 100;
        const newPrice =
            Math.round((price + parseFloat(((price * creditDaysInterest) / 360) * creditOpt)) * 100) / 100 +
            (commission || 0);
        return {
            priceAfterCredit: newPrice * volume,
            priceEarlyPaymentAmount: creditamount * volume,
        };
    }
    if (commissionType === 'transaction_based' || commissionType === 'lumpsum') {
        return {
            priceEarlyPaymentAmount:
                (Math.round(((price * creditDaysInterest) / 360) * creditOpt * 100) / 100) * volume,
            priceAfterCredit:
                (Math.round((price + parseFloat(((price * creditDaysInterest) / 360) * creditOpt)) * 100) / 100) *
                    volume +
                (commission || 0),
        };
    }
    return { priceAfterCredit: undefined, priceEarlyPaymentAmount: undefined };
};

export const accumulateBrokerProductValue = (products, creditDaysInterest, creditOpt) => {
    let totalProductValue = 0;
    products.forEach((product) => {
        const volume = parseFloat(product.volume);
        const price = parseFloat(product.price);
        const commission = parseFloat(product.commission);
        const commissionType = product.commission_type;

        if (!isNaN(volume) && !isNaN(price)) {
            const { priceAfterCredit, priceEarlyPaymentAmount } = getBrokerPriceAfterCredit(
                commissionType,
                price,
                volume,
                commission,
                creditDaysInterest,
                creditOpt
            );
            product.priceAfterCrdit = priceAfterCredit;
            product.creditDaysInterest = creditDaysInterest;
            product.priceEarlyPaymentAmount = priceEarlyPaymentAmount;
            totalProductValue += Math.round(priceAfterCredit * 100) / 100;
        }
    });
    return totalProductValue;
};

export const sumAdditionalChargeAmounts = (additionalCharges) => {
    let totalChargeValue = 0;
    additionalCharges.forEach((charge) => {
        const amount = parseFloat(charge.amount);
        if (!isNaN(amount)) {
            totalChargeValue += amount;
        }
    });
    return totalChargeValue;
};

export const getRoleAdjustedDealValue = (role, total, tradeOPsCharges, disputeCharges, totalFee) => {
    const roundedBase =
        Math.round(total * 100) / 100 +
        Math.round(tradeOPsCharges * 100) / 100 +
        Math.round(disputeCharges * 100) / 100;
    if (role === 'buyer' || role === 'broker') {
        return roundedBase + totalFee;
    }
    return roundedBase - totalFee;
};

export const accumulateProductValueForRole = (
    platformRole,
    products,
    creditDaysInterest,
    creditOpt,
    paymentTermVar,
    optionVariable
) => {
    if (platformRole === 'buyer') {
        return accumulateBuyerProductValue(products, creditDaysInterest, creditOpt);
    }
    if (platformRole === 'supplier') {
        return accumulateSupplierProductValue(products, creditDaysInterest, creditOpt, paymentTermVar, optionVariable);
    }
    if (platformRole === 'broker') {
        return accumulateBrokerProductValue(products, creditDaysInterest, creditOpt);
    }
    return 0;
};

export const calculateOptionalServiceFees = (
    values,
    total,
    products,
    feeDetails,
    transaction,
    setTradeCost,
    setDisputeClaimCost
) => {
    let tradeOPsCharges = 0;
    let disputeCharges = 0;
    if (values?.checked?.includes('trade')) {
        tradeOPsCharges = CalculateFees('trade_ops_service_fees', total, products, feeDetails, transaction);
        setTradeCost(tradeOPsCharges);
    }
    if (values?.checked?.includes('disputeClaim')) {
        disputeCharges = CalculateFees('dispute_claim_management_fees', total, products, feeDetails, transaction);
        setDisputeClaimCost(disputeCharges);
    }
    return { tradeOPsCharges, disputeCharges };
};

export const formatCalculatedDealValue = ({
    values,
    platformRole,
    creditDaysInterest,
    creditOpt,
    paymentTermVar,
    optionVariable,
    organizationDetails,
    transaction,
    setTot,
    setProd,
    setTradeCost,
    setDisputeClaimCost,
    setTotalDealVal,
}) => {
    const totalProductValue = accumulateProductValueForRole(
        platformRole,
        values.products,
        creditDaysInterest,
        creditOpt,
        paymentTermVar,
        optionVariable
    );
    const totalChargeValue = sumAdditionalChargeAmounts(values.additional_charges);
    const total = Math.round(totalProductValue * 100) / 100 + Math.round(totalChargeValue * 100) / 100;
    const products = values?.products || [];
    const calculatedPlatformFee = CalculateFeesSup(total, products, organizationDetails?.termSheet?.fee_details, transaction);
    setTot(total);
    setProd(products);
    const { tradeOPsCharges, disputeCharges } = calculateOptionalServiceFees(
        values,
        total,
        products,
        organizationDetails?.termSheet?.fee_details,
        transaction,
        setTradeCost,
        setDisputeClaimCost
    );
    const totalFee = calculatedPlatformFee.reduce((sum, fee) => sum + fee.fee_amount, 0);
    const totalDealVal = getRoleAdjustedDealValue(platformRole, total, tradeOPsCharges, disputeCharges, totalFee);
    const finalTotal = Math.round(totalDealVal * 100) / 100;
    setTotalDealVal(finalTotal);
    return PropertyFormat(finalTotal, amountFormat);
};

export const shouldAbortBrokerSubmit = ({
    platformRole,
    totalDealVal,
    buyerTradeLimit,
    localDealCalculation,
    setErrorMsg,
}) => {
    if (platformRole !== 'broker') {
        return false;
    }
    if (totalDealVal > buyerTradeLimit) {
        return true;
    }
    if (localDealCalculation?.total_partner_revenue <= 0) {
        toast.error('Revenue is less than or equal to 0. Please revisit your product margins', {
            icon: <span className='msg-icon float-start pe-2 sf-info-filled info-icon'></span>,
            autoClose: false,
            closeOnClick: true,
        });
        setErrorMsg('Revenue is less than or equal to 0. Please revisit your product margins');
        return true;
    }
    return false;
};

export const transformRequestProducts = (products) =>
    products.map((product) => ({
        product_ulid: product.type || product.product_ulid,
        volume: parseFloat(product.volume),
        min_volume: parseFloat(product.min_volume),
        amount: parseFloat(product.price),
        brokerage_commission: product.commission || 0,
        brokerage_commission_type: product.commission_type || null,
    }));

export const transformRequestAdditionalCharges = (additionalCharges) => {
    const allBlank = additionalCharges.every((charges) =>
        Object.entries(charges).every(([key, value]) => key === 'rowKey' || value === '')
    );
    if (allBlank) {
        return [];
    }
    return additionalCharges.map((charges) => ({
        charge_type: charges.charge_type,
        charge_amount: parseFloat(charges.charge_amount),
        product_ulid: charges.product_ulid ? charges.product_ulid : null,
        remarks: charges.remarks ? charges.remarks : '',
        other_charge_name: charges.other_charge_name ? charges.other_charge_name : '',
    }));
};

export const appendFocDocuments = (focDoc, formData) => {
    const documentUlids = [];
    if (!(focDoc && focDoc.length > 0)) {
        return documentUlids;
    }
    Array.from(focDoc).forEach((file) => {
        const fileToUpload = file.file || file;
        if (fileToUpload instanceof File) {
            formData.append('file', fileToUpload);
        } else if (file instanceof File) {
            formData.append('file', file);
        }
        if (file.document_ulid) {
            documentUlids.push({
                document_ulid: file.document_ulid,
                file_name: file.file_name || file.name || file.file?.name,
            });
        }
    });
    return documentUlids;
};

export const getBuyerOrganizationUlid = (platformRole, values, ctx) => {
    if (platformRole === 'supplier') {
        return (
            values?.buyer_supplier_name[0]?.organization_ulid || values?.buyer_supplier_name[0]?.buyer_organization_ulid
        );
    }
    if (platformRole === 'broker') {
        return ctx.associatedBuyer;
    }
    return null;
};

export const getSupplierOrganizationUlid = (platformRole, values, ctx) => {
    if (platformRole === 'buyer' || platformRole === 'broker') {
        return values?.buyer_supplier_name[0]?.organization_ulid || values?.buyer_supplier_name;
    }
    return getOrgUlid(ctx);
};

export const getEarlyPaymentPayloadValue = (platformRole, earlyPaymentOptions) => {
    if (platformRole !== 'supplier') {
        return '';
    }
    if (earlyPaymentOptions === '3_days') {
        return 'cod';
    }
    return earlyPaymentOptions;
};

export const getExtendedCreditPayloadValue = (platformRole, extendedCreditOption) => {
    if (platformRole === 'buyer' || platformRole === 'broker') {
        return extendedCreditOption;
    }
    return '';
};

export const getBrokerOrganizationUlid = (platformRole, ctx) => {
    if (platformRole === 'broker') {
        return ctx.userData.user_organizations[0].organization_ulid;
    }
    return null;
};

export const getBrokerUserUlid = (platformRole, ctx) => {
    if (platformRole === 'broker') {
        return ctx.userData.user_ulid;
    }
    return null;
};

export const getVesselImoValue = (vesselImo) => {
    if (vesselImo && vesselImo.includes('| IMO ')) {
        return vesselImo.split('| IMO ')[1];
    }
    return vesselImo;
};

export const buildDealFinancingPayload = ({ values, ctx, platformRole, documentUlids, totalDealVal }) => {
    const isTradeChecked = values?.checked?.includes('trade');
    const isDisputeClaimChecked = values?.checked?.includes('disputeClaim');
    return {
        type: 'deal_financing',
        buyer_organization_ulid: getBuyerOrganizationUlid(platformRole, values, ctx),
        supplier_organization_ulid: getSupplierOrganizationUlid(platformRole, values, ctx),
        port_ulid: values.port[0]?.ulid,
        next_port_ulid: values.next_port[0]?.ulid,
        expected_date_start: values.delivery_start_date,
        expected_date_end: values.delivery_end_date,
        payment_term: values.payment_term,
        delivery_mode: values.delivery_mode,
        agent: values.assign_agent ? values.assign_agent : null,
        broker: values.assign_broker ? values.assign_broker : null,
        document_description: values.doc_description,
        trade_ops_service_charge: isTradeChecked === undefined ? false : isTradeChecked,
        dispute_claims_management: isDisputeClaimChecked === undefined ? false : isDisputeClaimChecked,
        deal_specific_agreements: values.deal_specific_agreements,
        extended_credit_options: getExtendedCreditPayloadValue(platformRole, values.extended_credit_option),
        early_payment: getEarlyPaymentPayloadValue(platformRole, values.early_payment_options),
        settle_exceeded_credit: values.settle_exceeded_credit,
        status: 'active',
        user_ulid: ctx.userData.user_ulid,
        broker_organization_ulid: getBrokerOrganizationUlid(platformRole, ctx),
        broker_user_ulid: getBrokerUserUlid(platformRole, ctx),
        products: transformRequestProducts(values.products),
        charges: transformRequestAdditionalCharges(values.additional_charges),
        vessel_ulid: values.vessel_ulid,
        vessel_imo: getVesselImoValue(values.vessel_imo),
        organization_ulid: getOrgUlid(ctx),
        total_deal_value: Math.round(totalDealVal * 100) / 100,
        documents: documentUlids,
    };
};

export const getValidationSchemaForRole = (platformRole) => {
    if (platformRole === 'broker') {
        return ValidationSchemaForRequestFinancing;
    }
    if (platformRole === 'supplier') {
        return ValidationSchemaForRequestFinancingSupplier;
    }
    return ValidationSchemaForRequestFinancingBuyer;
};

export const resolveDealPartyIds = ({ platformRole, orgId, associatedBuyer, buyerSupplierName }) => {
    let buyerId = null;
    let supId = null;
    let brokId = null;
    switch (platformRole) {
        case 'buyer':
            buyerId = orgId;
            break;
        case 'supplier':
            supId = orgId;
            buyerId = buyerSupplierName?.[0]?.buyer_organization_ulid;
            break;
        case 'broker':
            buyerId = associatedBuyer;
            brokId = orgId;
            break;
        default:
            break;
    }
    return { buyerId, supId, brokId };
};

export const mapDealCalculationProducts = (products) =>
    (products || []).map((p) => ({
        product_ulid: p.type,
        price: Number(p.price),
        volume: Number(p.volume),
        brokerage_commission: p.commission || 0,
        product_name: p.name || '',
        brokerage_commission_type: p.commission_type || '',
    }));

export const mapDealCalculationProductsWithParsedCommission = (products) =>
    (products || []).map((p) => ({
        product_ulid: p.type,
        price: Number(p.price),
        volume: Number(p.volume),
        brokerage_commission: parseFloat(p.commission) || 0,
        product_name: p.name || '',
        brokerage_commission_type: p.commission_type || '',
    }));

export const mapDealCalculationCharges = (charges, productNameKey = 'product_name') =>
    charges?.map((charge) => ({
        product_name: productNameKey === 'name' ? charge.name || '' : charge.product_name,
        charge_type: charge.charge_type || '',
        charge_amount: Number(charge.charge_amount) || 0,
        product_ulid: charge.product_ulid || '',
        other_charge_name: charge.other_charge_name || '',
    })) || [];

export const buildDealCalculationPayload = ({
    platformRole,
    orgId,
    associatedBuyer,
    buyerSupplierName,
    products,
    charges,
    paymentTerm,
    earlyPayment,
    extendedCredit,
    checked,
    transactionType,
    productMapper = mapDealCalculationProducts,
    chargeProductNameKey = 'product_name',
    includeRoleKey = 'role',
}) => {
    const { buyerId, supId, brokId } = resolveDealPartyIds({
        platformRole,
        orgId,
        associatedBuyer,
        buyerSupplierName,
    });
    const payload = {
        buyer_organization_ulid: buyerId || '',
        supplier_organization_ulid: supId || '',
        broker_organization_ulid: brokId || '',
        products: productMapper(products),
        is_tradeops: (checked ?? []).includes('trade'),
        is_dispute_claim_management: (checked ?? []).includes('disputeClaim'),
        transaction_type: transactionType,
        payment_term: paymentTerm || '',
        early_payment: earlyPayment || '',
        extended_credit: extendedCredit || '',
        additional_charges: mapDealCalculationCharges(charges, chargeProductNameKey),
    };
    payload[includeRoleKey] = platformRole;
    return payload;
};
